import { useState } from 'react';
import { useAuth } from '../store/auth';
import { clsx } from 'clsx';

export default function LoginPage() {
  const { login, register, sendRegistrationOtp, loading, error, clearError } = useAuth();
  const [epost, setEpost] = useState('');
  const [deviceNavn, setDeviceNavn] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [regStep, setRegStep] = useState<'email' | 'otp'>('email');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!epost) return;
    try {
      if (mode === 'login') {
        await login(epost);
      } else if (regStep === 'email') {
        await sendRegistrationOtp(epost);
        setRegStep('otp');
      } else {
        await register(epost, otpCode, deviceNavn || undefined);
      }
    } catch {
      // Feil håndteres i store
    }
  };

  const switchMode = (m: 'login' | 'register') => {
    setMode(m);
    setRegStep('email');
    setOtpCode('');
    clearError();
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-950 px-4">
      {/* Animert mesh-bakgrunn */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-mesh-dark" />
      <div className="pointer-events-none absolute -left-40 -top-40 h-96 w-96 animate-pulse-soft rounded-full bg-brand-600/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 h-96 w-96 animate-pulse-soft rounded-full bg-accent-600/20 blur-3xl" />

      <div className="relative w-full max-w-md animate-fade-in-up">
        {/* Logo + tittel */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-brand shadow-glow">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="h-10 w-10 text-white"
            >
              <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7" />
              <path d="M3 7l2-4h14l2 4" />
              <path d="M3 7h18" />
              <path d="M8 12h8" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white">
            Ticket System
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            Logg inn med sikkerhetsnøkkel
          </p>
        </div>

        {/* Glassmorphism-kort */}
        <div className="glass rounded-2xl p-6 shadow-soft-lg">
          {/* Mode-veksling */}
          <div className="mb-6 flex rounded-xl bg-slate-100/80 p-1 dark:bg-slate-800/80">
            <button
              type="button"
              onClick={() => switchMode('login')}
              className={clsx(
                'flex-1 rounded-lg py-2 text-sm font-medium transition-all',
                mode === 'login'
                  ? 'bg-white text-slate-900 shadow-soft dark:bg-slate-700 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400',
              )}
            >
              Logg inn
            </button>
            <button
              type="button"
              onClick={() => switchMode('register')}
              className={clsx(
                'flex-1 rounded-lg py-2 text-sm font-medium transition-all',
                mode === 'register'
                  ? 'bg-white text-slate-900 shadow-soft dark:bg-slate-700 dark:text-white'
                  : 'text-slate-500 dark:text-slate-400',
              )}
            >
              Registrer nøkkel
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* E-postfelt – alltid synlig, men deaktivert i OTP-steg */}
            <div>
              <label htmlFor="epost" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                E-post
              </label>
              <input
                id="epost"
                type="email"
                autoComplete="email"
                required
                value={epost}
                onChange={(e) => setEpost(e.target.value)}
                placeholder="navn@firma.no"
                className="input"
                disabled={loading || (mode === 'register' && regStep === 'otp')}
              />
            </div>

            {/* OTP-steg for registrering */}
            {mode === 'register' && regStep === 'otp' && (
              <div className="animate-fade-in-down space-y-4">
                <div className="rounded-xl bg-brand-50/50 p-3 text-sm text-slate-600 dark:bg-brand-500/5 dark:text-slate-300">
                  Vi har sendt en 6-sifret kode til <strong>{epost}</strong>. Sjekk innboksen din.
                </div>
                <div>
                  <label htmlFor="otpCode" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                    Verifiseringskode
                  </label>
                  <input
                    id="otpCode"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    required
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="123456"
                    className="input text-center text-2xl tracking-[0.5em]"
                    disabled={loading}
                    autoFocus
                  />
                </div>
                <div>
                  <label
                    htmlFor="deviceNavn"
                    className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200"
                  >
                    Navn på nøkkel (valgfritt)
                  </label>
                  <input
                    id="deviceNavn"
                    type="text"
                    value={deviceNavn}
                    onChange={(e) => setDeviceNavn(e.target.value)}
                    placeholder="f.eks. YubiKey, iPhone, …"
                    className="input"
                    disabled={loading}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setRegStep('email');
                    setOtpCode('');
                    clearError();
                  }}
                  className="text-xs text-slate-400 hover:text-slate-500"
                >
                  ← Tilbake til e-post
                </button>
              </div>
            )}

            {error && (
              <div className="animate-fade-in rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={loading || !epost || (mode === 'register' && regStep === 'otp' && otpCode.length !== 6)}
            >
              {loading ? (
                <>
                  <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  {mode === 'register' && regStep === 'email' ? 'Sender kode…' : 'Venter på sikkerhetsnøkkel…'}
                </>
              ) : mode === 'login' ? (
                <>
                  <KeyIcon /> Logg inn med passkey
                </>
              ) : regStep === 'email' ? (
                <>
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <path d="m22 6-10 7L2 6" />
                  </svg>
                  Send verifiseringskode
                </>
              ) : (
                <>
                  <KeyIcon /> Registrer sikkerhetsnøkkel
                </>
              )}
            </button>
          </form>

          <p className="mt-5 text-center text-xs text-slate-400 dark:text-slate-500">
            {mode === 'login'
              ? 'Bruk Touch ID, Face ID, Windows Hello eller en fysisk YubiKey.'
              : regStep === 'email'
                ? 'Første gang? Be admin opprette brukerkonto, deretter registrer en passkey.'
                : 'Skriv inn koden fra e-posten for å bekrefte identiteten din.'}
          </p>
        </div>

        {/* Link til offentlig innsending */}
        <p className="mt-6 text-center text-sm text-slate-500">
          Er du kunde?{' '}
          <a href="/submit" className="font-medium text-brand-400 hover:text-brand-300 hover:underline">
            Send inn henvendelse her
          </a>
        </p>
      </div>
    </div>
  );
}

function KeyIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className="h-4 w-4"
    >
      <circle cx="7.5" cy="15.5" r="5.5" />
      <path d="m21 2-9.6 9.6" />
      <path d="m15.5 7.5 3 3L22 7l-3-3" />
    </svg>
  );
}
